import { Component, ErrorInfo, ReactNode } from 'react';
import { ApiError, AppError } from '@/utils/errorHandling';
import styles from '@/styles/components/ErrorBoundary.module.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorType: 'api' | 'network' | 'validation' | 'auth' | 'generic';
  userMessage: string;
  canRetry: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    errorType: 'generic',
    userMessage: 'Something went wrong',
    canRetry: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    console.log('ErrorBoundary caught error:', error);

    // Handle ApiError instances from generated client
    if (error instanceof ApiError) {
      const errorType = error.isNetworkError 
        ? 'network' 
        : error.isValidationError 
        ? 'validation'
        : error.isAuthError()
        ? 'auth'
        : 'api';

      return {
        hasError: true,
        error,
        errorType,
        userMessage: error.getUserMessage(),
        canRetry: !error.isAuthError() && !error.isValidationError,
      };
    }

    // Handle AppError instances
    if (error instanceof AppError) {
      return {
        hasError: true,
        error,
        errorType: 'generic',
        userMessage: error.message,
        canRetry: true,
      };
    }

    // Handle generic errors
    return {
      hasError: true,
      error,
      errorType: 'generic',
      userMessage: 'An unexpected error occurred. Please try again.',
      canRetry: true,
    };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Send error to monitoring service
    // reportError(error, errorInfo);

    // Log additional context for ApiError instances
    if (error instanceof ApiError) {
      console.error('API Error Details:', {
        statusCode: error.statusCode,
        code: error.code,
        context: error.context,
        isNetworkError: error.isNetworkError,
        isValidationError: error.isValidationError,
        validationErrors: error.getValidationErrors(),
      });
    }
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      errorType: 'generic',
      userMessage: 'Something went wrong',
      canRetry: false,
    });
  };

  private handleReload = () => {
    window.location.reload();
  };

  private renderErrorContent() {
    const { error, errorType, userMessage, canRetry } = this.state;

    // Custom fallback if provided
    if (this.props.fallback) {
      return this.props.fallback;
    }

    // Render different UI based on error type
    switch (errorType) {
      case 'network':
        return (
          <div className={styles.container}>
            <div className={styles.icon}>🌐</div>
            <h2 className={styles.title}>Connection Problem</h2>
            <p className={styles.message}>{userMessage}</p>
            <div className={styles.buttonContainer}>
              {canRetry && (
                <button onClick={this.handleRetry} className={`${styles.button} ${styles.primaryButton}`}>
                  Try Again
                </button>
              )}
              <button onClick={this.handleReload} className={`${styles.button} ${styles.secondaryButton}`}>
                Reload Page
              </button>
            </div>
          </div>
        );

      case 'auth':
        return (
          <div className={styles.container}>
            <div className={styles.icon}>🔒</div>
            <h2 className={styles.title}>Authentication Required</h2>
            <p className={styles.message}>{userMessage}</p>
            <div className={styles.buttonContainer}>
              <button 
                onClick={() => window.location.href = '/login'} 
                className={`${styles.button} ${styles.primaryButton}`}
              >
                Go to Login
              </button>
              <button onClick={this.handleReload} className={`${styles.button} ${styles.secondaryButton}`}>
                Reload Page
              </button>
            </div>
          </div>
        );

      case 'validation':
        const apiError = error as ApiError;
        const validationErrors = apiError.getValidationErrors();
        
        return (
          <div className={styles.container}>
            <div className={styles.icon}>⚠️</div>
            <h2 className={styles.title}>Invalid Data</h2>
            <p className={styles.message}>{userMessage}</p>
            {validationErrors && (
              <div className={styles.validationErrors}>
                <h3>Please fix the following issues:</h3>
                <ul>
                  {Object.entries(validationErrors).map(([field, errors]) => (
                    <li key={field}>
                      <strong>{field}:</strong> {errors.join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className={styles.buttonContainer}>
              <button onClick={this.handleRetry} className={`${styles.button} ${styles.primaryButton}`}>
                Try Again
              </button>
            </div>
          </div>
        );

      case 'api':
        return (
          <div className={styles.container}>
            <div className={styles.icon}>⚡</div>
            <h2 className={styles.title}>Service Error</h2>
            <p className={styles.message}>{userMessage}</p>
            <div className={styles.buttonContainer}>
              {canRetry && (
                <button onClick={this.handleRetry} className={`${styles.button} ${styles.primaryButton}`}>
                  Try Again
                </button>
              )}
              <button onClick={this.handleReload} className={`${styles.button} ${styles.secondaryButton}`}>
                Reload Page
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div className={styles.container}>
            <div className={styles.icon}>❌</div>
            <h2 className={styles.title}>Something went wrong</h2>
            <p className={styles.message}>{userMessage}</p>
            <div className={styles.buttonContainer}>
              {canRetry && (
                <button onClick={this.handleRetry} className={`${styles.button} ${styles.primaryButton}`}>
                  Try Again
                </button>
              )}
              <button onClick={this.handleReload} className={`${styles.button} ${styles.secondaryButton}`}>
                Reload Page
              </button>
            </div>
          </div>
        );
    }
  }



  public override render() {
    if (this.state.hasError) {
      return this.renderErrorContent();
    }

    return this.props.children;
  }
}
